import {Component, inject, OnInit, ViewChild, AfterViewInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Router, ActivatedRoute, RouterLink} from "@angular/router";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {BroadcastNotificationService} from "../../services/broadcast-notification.service";
import {SnackbarService} from "../../services/snackbar.service";
import {ICreateBroadcastNotification} from "../../interfaces/notifications/ICreateBroadcastNotification";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {ChooseYourGameComponent} from "../../components/choose-your-game/choose-your-game.component";
import {BroadcastNotificationScopeTypes} from "../../enum/BroadcastNotificationScopeTypes";
import {BroadcastNotificationTypes} from "../../enum/BroadcastNotificationTypes";
import {IBroadcastNotification} from "../../interfaces/notifications/IBroadcastNotification";
import {GameService} from "../../services/game.service";
import {PermissionsService} from "../../services/permissions.service";
import {ContainerComponent} from "@coreui/angular";
import {MatCard, MatCardContent} from "@angular/material/card";
import {MatStep, MatStepLabel, MatStepper, MatStepperNext, MatStepperPrevious} from "@angular/material/stepper";
import {NgIf} from "@angular/common";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatDivider} from "@angular/material/divider";
import {MatButton} from "@angular/material/button";
import {MatInput} from "@angular/material/input";
import {NgxColorsModule} from "ngx-colors";
import {MatCheckbox} from "@angular/material/checkbox";
import {BroadcastNotificationPreviewComponent} from "../../components/broadcast-notification-preview/broadcast-notification-preview.component";
import {MatIcon} from "@angular/material/icon";
import {IApiErrorResponse} from "../../interfaces/error/IApiErrorResponse";
import {StepperSelectionEvent} from "@angular/cdk/stepper";

@Component({
  selector: 'app-create-broadcast-notification',
  standalone: true,
  imports: [
    ContainerComponent,
    MatCard,
    MatCardContent,
    MatStepper,
    MatStep,
    ReactiveFormsModule,
    NgIf,
    MatStepLabel,
    MatFormField,
    MatSelect,
    MatOption,
    ChooseYourGameComponent,
    MatError,
    MatDivider,
    MatButton,
    MatStepperNext,
    MatInput,
    NgxColorsModule,
    MatStepperPrevious,
    MatCheckbox,
    BroadcastNotificationPreviewComponent,
    MatLabel,
    MatHint,
    MatIcon,
    RouterLink
  ],
  templateUrl: './create-broadcast-notification.component.html',
  styleUrl: './create-broadcast-notification.component.css'
})
export class CreateBroadcastNotificationComponent implements OnInit, AfterViewInit {
  @ViewChild('gameSelector') gameSelector!: ChooseYourGameComponent;
  @ViewChild('stepper') stepper!: MatStepper;

  uiState = UIStateEnum.ShowData;
  uiStateForTemplate = UIStateEnum;
  selectedGameName = '';
  isCausableAdmin = false;
  isEditMode = false;
  editingNotificationId?: string;

  // Cached preview notification - updated when entering preview step
  // This provides more stable binding than calling buildNotificationFromForm() in template
  previewNotification: Partial<IBroadcastNotification> | null = null;

  BroadcastNotificationScopeTypes = BroadcastNotificationScopeTypes;
  BroadcastNotificationTypes = BroadcastNotificationTypes;

  targetFormGroup = new FormGroup({
    scope: new FormControl(BroadcastNotificationScopeTypes.System, [Validators.required]),
    game: new FormControl('')
  });

  messageFormGroup = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    message: new FormControl('', [Validators.required]),
    type: new FormControl(BroadcastNotificationTypes.Banner, [Validators.required]),
    backgroundColor: new FormControl(''),
    textColor: new FormControl('')
  });

  scheduleFormGroup = new FormGroup({
    startDateTime: new FormControl('', [Validators.required]),
    endDateTime: new FormControl('', [Validators.required]),
    isDismissible: new FormControl(true)
  });

  private broadcastNotificationService = inject(BroadcastNotificationService);
  private snackBarService = inject(SnackbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private gameService = inject(GameService);
  private permissionsService = inject(PermissionsService);

  ngOnInit() {
    this.checkPermissions();
    this.setDefaultDates();
    this.setupGameValidation();
    this.handleRouteParams();
  }

  ngAfterViewInit() {
    // Subscribe to stepper selection changes to update preview when entering preview step
    this.stepper.selectionChange.subscribe((event: StepperSelectionEvent) => {
      this.onStepChange(event);
    });
  }

  /**
   * Handle stepper step changes - update preview notification when entering preview step
   */
  onStepChange(event: StepperSelectionEvent) {
    // Check if we're entering the preview step (last step)
    // Use selectedIndex to check if we're on the last step
    const totalSteps = this.stepper.steps.length;
    const isLastStep = event.selectedIndex === totalSteps - 1;
    if (isLastStep) {
      this.updatePreviewNotification();
    }
  }

  /**
   * Update the cached preview notification from form values
   * This is called when entering the preview step to ensure the preview shows current form data
   */
  updatePreviewNotification() {
    this.previewNotification = this.buildNotificationFromForm();
  }

  private checkPermissions() {
    this.permissionsService.hasCausableAdminPermissions().subscribe(hasAccess => {
      this.isCausableAdmin = hasAccess;
    });
  }

  private setDefaultDates() {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    this.scheduleFormGroup.patchValue({
      startDateTime: this.formatDateTime(now),
      endDateTime: this.formatDateTime(tomorrow)
    });
  }

  private setupGameValidation() {
    this.targetFormGroup.get('scope')?.valueChanges.subscribe(scope => {
      const gameControl = this.targetFormGroup.get('game');
      if (scope === BroadcastNotificationScopeTypes.Game) {
        gameControl?.setValidators([Validators.required]);
      } else {
        gameControl?.clearValidators();
        gameControl?.setValue('');
      }
      gameControl?.updateValueAndValidity();
    });
  }

  private handleRouteParams() {
    const notificationId = this.route.snapshot.params['id'];
    const gameId = this.route.snapshot.params['gameId'];

    if (notificationId) {
      this.loadForEdit(notificationId);
    } else if (gameId) {
      this.targetFormGroup.patchValue({
        scope: BroadcastNotificationScopeTypes.Game,
        game: gameId
      });
    }

    this.selectedGameName = this.gameService.getAllAvailableGames().find(g => g.Id === gameId)?.Name || '';
  }

  get shouldShowTargetStep() {
    return this.route.snapshot.data['allowScopeSelection'] ?? false;
  }

  private loadForEdit(id: string) {
    this.isEditMode = true;
    this.editingNotificationId = id;
    this.uiState = UIStateEnum.ShowLoading;

    const gameId = this.route.snapshot.params['gameId'];

    this.broadcastNotificationService.getBroadcastNotification(id, gameId).subscribe({
      next: (notification) => {
        this.populateForm(notification);
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar("Failed to load notification");
        this.router.navigate(['/manage-broadcast-notifications']);
      }
    });
  }

  private populateForm(notification: IBroadcastNotification) {
    this.targetFormGroup.patchValue({
      scope: notification.GameId ? BroadcastNotificationScopeTypes.Game : BroadcastNotificationScopeTypes.System,
      game: notification.GameId || ''
    });

    this.messageFormGroup.patchValue({
      title: notification.Title,
      message: notification.Message,
      type: notification.BroadcastNotificationTypeId,
      backgroundColor: notification.BackgroundColor || '',
      textColor: notification.TextColor || ''
    });

    this.scheduleFormGroup.patchValue({
      startDateTime: this.formatDateTime(new Date(notification.StartDate)),
      endDateTime: this.formatDateTime(new Date(notification.EndDate)),
      isDismissible: notification.IsDismissible
    });

    // Set game in component if exists
    if (notification.GameId) {
      const game = this.gameService.getAllAvailableGames().find(g => g.Id === notification.GameId);
      if (game) {
        this.selectedGameName = game.Name;
        setTimeout(() => {
          if (this.gameSelector) {
            this.gameSelector.chosenGame = game;
            this.gameSelector.onGameChange(game);
          }
        });
      }
    }
  }

  onGameSelected(game: IGameQueryResult) {
    this.targetFormGroup.get('game')?.setValue(game.Id);
    this.selectedGameName = game.Name;
  }

  onScopeChange() {
    if (this.targetFormGroup.get('scope')?.value === BroadcastNotificationScopeTypes.System) {
      this.targetFormGroup.get('game')?.setValue('');
    }
  }

  createBroadcastNotification() {
    if (!this.targetFormGroup.valid || !this.messageFormGroup.valid || !this.scheduleFormGroup.valid) {
      this.snackBarService.openErrorSnackBar("Please fill in all required fields");
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const scope = this.targetFormGroup.get('scope')?.value!;
    const gameId = this.targetFormGroup.get('game')?.value;

    const command: ICreateBroadcastNotification = {
      Title: this.messageFormGroup.get('title')?.value!,
      Message: this.messageFormGroup.get('message')?.value!,
      GameId: scope === BroadcastNotificationScopeTypes.Game ? gameId! : undefined,
      BroadcastNotificationScopeTypeId: scope,
      BroadcastNotificationTypeId: this.messageFormGroup.get('type')?.value!,
      BackgroundColor: this.messageFormGroup.get('backgroundColor')?.value || undefined,
      TextColor: this.messageFormGroup.get('textColor')?.value || undefined,
      IsDismissible: this.scheduleFormGroup.get('isDismissible')?.value!,
      StartDate: this.scheduleFormGroup.get('startDateTime')?.value!,
      EndDate: this.scheduleFormGroup.get('endDateTime')?.value!,
    };

    const operation = this.isEditMode
      ? this.broadcastNotificationService.updateBroadcastNotification(this.editingNotificationId!, command)
      : this.broadcastNotificationService.createBroadcastNotification(command);

    operation.subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackBarService.openSuccessfulSnackBar(`Successfully ${this.isEditMode ? 'Updated' : 'Created'} Broadcast Notification`);
        this.router.navigate(['/manage-broadcast-notifications']);
      },
      error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        console.log('Error creating/updating notification:', res);
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackBarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackBarService.openErrorSnackBar(`Failed to ${this.isEditMode ? 'Update' : 'Create'} Broadcast Notification`);
        }
      }
    });
  }

  resetForm() {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    this.targetFormGroup.reset({
      scope: BroadcastNotificationScopeTypes.System,
      game: ''
    });

    this.messageFormGroup.reset({
      title: '',
      message: '',
      type: BroadcastNotificationTypes.Banner,
      backgroundColor: '',
      textColor: ''
    });

    this.scheduleFormGroup.reset({
      startDateTime: this.formatDateTime(now),
      endDateTime: this.formatDateTime(tomorrow),
      isDismissible: true,
    });

    this.stepper.reset();
  }

  buildNotificationFromForm(): Partial<IBroadcastNotification> {
    const scope = this.targetFormGroup.get('scope')?.value;
    const gameId = this.targetFormGroup.get('game')?.value;

    return {
      Title: this.messageFormGroup.get('title')?.value || '',
      Message: this.messageFormGroup.get('message')?.value || '',
      GameId: scope === BroadcastNotificationScopeTypes.Game ? (gameId || undefined) : undefined,
      BroadcastNotificationScopeTypeId: scope || BroadcastNotificationScopeTypes.System,
      BroadcastNotificationTypeId: this.messageFormGroup.get('type')?.value || BroadcastNotificationTypes.Banner,
      BackgroundColor: this.messageFormGroup.get('backgroundColor')?.value || undefined,
      TextColor: this.messageFormGroup.get('textColor')?.value || undefined,
      IsDismissible: this.scheduleFormGroup.get('isDismissible')?.value ?? true,
      StartDate: this.scheduleFormGroup.get('startDateTime')?.value || new Date().toISOString(),
      EndDate: this.scheduleFormGroup.get('endDateTime')?.value || new Date().toISOString()
    };
  }

  private formatDateTime(date: Date): string {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
}
